import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { Clock } from 'lucide-react';
import './TimeMetricsChart.css';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="time-tooltip">
        <p className="tooltip-stage">{data.name}</p>
        <p className="tooltip-days">{data.avgDays.toFixed(1)} days average</p>
        {data.minDays !== undefined && data.maxDays !== undefined && (
          <p className="tooltip-range">
            Range: {data.minDays} - {data.maxDays} days
          </p>
        )}
      </div>
    );
  }
  return null;
};

const TimeSkeleton = () => {
  const barHeights = [65, 45, 80, 55, 70, 40];
  return (
    <div className="time-skeleton">
      <div className="time-skeleton-chart">
        {barHeights.map((height, index) => (
          <div key={index} className="time-skeleton-bar-wrapper">
            <div className="time-skeleton-bar" style={{ height: `${height}%` }} />
            <div className="time-skeleton-bar-label" />
          </div>
        ))}
      </div>
      <div className="time-skeleton-summary">
        <div className="time-skeleton-stat">
          <div className="time-skeleton-stat-label" />
          <div className="time-skeleton-stat-value" />
        </div>
        <div className="time-skeleton-stat">
          <div className="time-skeleton-stat-label" />
          <div className="time-skeleton-stat-value" />
        </div>
        <div className="time-skeleton-stat">
          <div className="time-skeleton-stat-label" />
          <div className="time-skeleton-stat-value" />
        </div>
      </div>
    </div>
  );
};

const TimeMetricsChart = ({ data, isLoading }) => {
  // Show skeleton while loading
  if (isLoading) {
    return <TimeSkeleton />;
  }

  // Handle nested data structure from API: { stageDurations: [...], timeToFill: {...}, ... }
  const stages = data?.stageDurations || [];
  const avgTimeToFill = data?.timeToFill?.avgDays;

  if (!stages || stages.length === 0) {
    return (
      <div className="time-empty">
        <Clock className="time-empty-icon" />
        <p className="time-empty-title">No Time Metrics Yet</p>
        <p className="time-empty-message">
          Time metrics will appear here once candidates start moving through pipeline stages.
        </p>
      </div>
    );
  }

  // Calculate total time in pipeline
  const totalPipelineTime = stages.reduce((sum, stage) => sum + (stage.avgDays || 0), 0);

  // Find longest stage
  const longestStage = stages.reduce((longest, current) =>
    (current.avgDays > (longest?.avgDays || 0)) ? current : longest
  , null);

  return (
    <div className="time-chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={stages}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fontFamily: 'Fira Sans' }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 12, fontFamily: 'Fira Code' }}
            label={{
              value: 'Days',
              angle: -90,
              position: 'insideLeft',
              style: { fontFamily: 'Fira Sans', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {avgTimeToFill && (
            <ReferenceLine
              y={avgTimeToFill}
              stroke="#F59E0B"
              strokeDasharray="5 5"
              label={{
                value: `Avg: ${avgTimeToFill}d`,
                position: 'right',
                style: { fontSize: 11, fontFamily: 'Fira Code', fill: '#F59E0B' }
              }}
            />
          )}
          <Bar
            dataKey="avgDays"
            radius={[4, 4, 0, 0]}
            barSize={32}
          >
            {stages.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || '#3B82F6'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Time summary */}
      <div className="time-summary">
        {avgTimeToFill !== undefined && avgTimeToFill !== null && (
          <div className="time-summary-item highlight">
            <span className="summary-label">Avg. Time to Fill</span>
            <span className="summary-value">{avgTimeToFill} days</span>
          </div>
        )}
        <div className="time-summary-item">
          <span className="summary-label">Total Pipeline Time</span>
          <span className="summary-value">{totalPipelineTime.toFixed(1)} days</span>
        </div>
        {longestStage && (
          <div className="time-summary-item">
            <span className="summary-label">Longest Stage</span>
            <span className="summary-value">{longestStage.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeMetricsChart;
